import React, { useEffect, useMemo, useState } from 'react';
import { BsCalendarFill } from 'react-icons/bs';
import { FaArrowLeft, FaBell, FaChartLine, FaChevronDown, FaChevronRight, FaEdit, FaPlus, FaTools, FaTrash, FaUsers, FaWpforms } from 'react-icons/fa';
import { IoDocuments } from 'react-icons/io5';
import { MdComputer } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { userAppData } from '../MainApp.js';
import Header from '../common/header.js';
import PolarSwitch from '../common/formComponents/polarSwitch.js';
import LoadingComp from '../common/loading.js';
import { BULK_EMAIL_TOOL_GUID } from '../tools/toolRegistry.js';
import { getSysManagerGroups, updateSysManagerGroups } from './sysManagerGroupsApi.js';
import './SysManagerGroups.css';

const NO_GROUP_ID = '';
const TOAST_CONFIG = { autoClose: 4000, position: 'top-right' };
const BOOKING_ACCESS_OPTIONS = [
  { value: 'none', label: 'No Access' },
  { value: 'view', label: 'View' },
  { value: 'edit', label: 'Edit' },
  { value: 'manage', label: 'Manage' },
];
const ROTA_ACCESS_OPTIONS = [
  { value: 'none', label: 'No Access' },
  { value: 'view_self_only', label: 'View Own' },
  { value: 'view_all', label: 'View All' },
  { value: 'edit', label: 'Edit' },
];
const GROUP_PERMISSION_MODULES = [
  {
    key: 'alertData',
    label: 'Alerts',
    icon: FaBell,
    description: 'Access the Alerts module and manage school alerts.',
    options: [
      ['canManageAlerts', 'Manage alerts'],
      ['restrictEndedAlertsToOwn', 'Restrict ended alerts to own'],
      ['canViewReports', 'View reports'],
      ['canArchiveAlerts', 'Archive alerts'],
    ],
  },
  {
    key: 'onCallData',
    label: 'OnCall',
    icon: FaBell,
    description: 'Access calls, management tools and reporting.',
    options: [
      ['allowManageCalls', 'Manage calls'],
      ['canProxySubmit', 'Submit calls for others'],
      ['canViewReports', 'View reports'],
      ['canExportData', 'Export data'],
      ['canViewWholeSchoolData', 'View whole-school data'],
      ['canViewStaffReports', 'View staff reports'],
    ],
  },
  {
    key: 'itRequestsData',
    label: 'IT Requests',
    icon: MdComputer,
    description: 'Access IT requests, management tools and reporting.',
    options: [
      ['allowManageRequests', 'Manage requests'],
      ['restrictManageRequestsToOwn', 'Restrict management to own requests'],
      ['canViewReports', 'View reports'],
      ['canExportData', 'Export data'],
      ['canViewWholeSchoolData', 'View whole-school data'],
      ['notifications.newRequest', 'New request notifications'],
      ['notifications.updatedRequest', 'Updated request notifications'],
      ['notifications.completedRequest', 'Completed request notifications'],
    ],
  },
  {
    key: 'reproRequestsData',
    label: 'Reprographics',
    icon: IoDocuments,
    description: 'Access Reprographics requests, management tools and reporting.',
    options: [
      ['allowManageRequests', 'Manage requests'],
      ['restrictManageRequestsToOwn', 'Restrict management to own requests'],
      ['canViewReports', 'View reports'],
      ['canExportData', 'Export data'],
      ['notifications.newRequest', 'New request notifications'],
      ['notifications.updatedRequest', 'Updated request notifications'],
      ['notifications.completedRequest', 'Completed request notifications'],
    ],
  },
  {
    key: 'bookingsData',
    label: 'Bookings',
    icon: BsCalendarFill,
    description: 'Allows users to open Bookings. What they can see or change is controlled by the access settings below.',
    options: [
      [
        'bulkBookingAllowed',
        'Create bulk bookings',
        'Allows users to create the same booking across multiple dates or time slots in one action.',
      ],
      [
        'proxyBookAllowed',
        'Book for other users',
        'Allows users to create a booking on behalf of another staff member.',
      ],
    ],
  },
  {
    key: 'formsData',
    label: 'Forms',
    icon: FaWpforms,
    description: 'Access Forms and form creation.',
    options: [['canCreateForms', 'Create forms']],
  },
  {
    key: 'performanceManagement',
    label: 'Performance Management',
    icon: FaChartLine,
    description: 'Access personal Performance Management records and assigned templates.',
    options: [],
  },
  {
    key: 'rotasData',
    label: 'Rotas',
    icon: BsCalendarFill,
    description: 'Access rota families, view assignments and submit cover overrides.',
    options: [],
  },
  {
    key: 'toolsData',
    label: 'Tools',
    icon: FaTools,
    description: 'Access Tools and its shared options.',
    options: [],
  },
];

/** Return API errors in the application's UK English wording. */
function getGroupsErrorMessage(error, fallbackMessage) {
  const message = String(error?.message || '').trim();
  return message.toLowerCase() === 'unauthorized' ? 'Unauthorised' : (message || fallbackMessage);
}

/** Read a Boolean-like permission, including nested notification settings. */
function getPermissionEnabled(permissions, moduleKey, permission) {
  const modulePermissions = permissions?.[moduleKey] || {};
  const value = permission.startsWith('notifications.')
    ? modulePermissions?.notifications?.[permission.split('.', 2)[1]]
    : modulePermissions?.[permission];
  return value === true || Number(value) === 1;
}

/** Return one booking group's valid access level or No Access. */
function getBookingGroupAccess(permissions, bookingGroupId) {
  const groupAccess = permissions?.bookingsData?.groupAccess;
  if (!Array.isArray(groupAccess)) {
    return 'none';
  }
  const matchingAccess = groupAccess.find((entry) => String(entry?.id || '') === bookingGroupId);
  const accessLevel = String(matchingAccess?.perms || 'none').toLowerCase();
  return BOOKING_ACCESS_OPTIONS.some((option) => option.value === accessLevel)
    ? accessLevel
    : 'none';
}

/** Return whether attendees are enabled for one booking-group entry. */
function getBookingGroupAttendees(permissions, bookingGroupId) {
  const groupAccess = permissions?.bookingsData?.groupAccess;
  if (!Array.isArray(groupAccess)) {
    return false;
  }
  const matchingAccess = groupAccess.find((entry) => String(entry?.id || '') === bookingGroupId);
  return matchingAccess?.allowAttendees === true;
}

/** Return one room's valid explicit access level or No Access. */
function getBookingRoomAccess(permissions, roomId) {
  const roomAccess = permissions?.bookingsData?.roomAccess;
  if (!Array.isArray(roomAccess)) {
    return 'none';
  }
  const matchingAccess = roomAccess.find((entry) => String(entry?.id || '') === roomId);
  const accessLevel = String(matchingAccess?.perms || 'none').toLowerCase();
  return BOOKING_ACCESS_OPTIONS.some((option) => option.value === accessLevel)
    ? accessLevel
    : 'none';
}

/** Return one rota family's inherited access map entry. */
function getRotaAccess(permissions, masterRotaId) {
  const access = permissions?.rotasData?.access;
  if (access && !Array.isArray(access) && typeof access === 'object') {
    return access[masterRotaId] || {};
  }
  return Array.isArray(access)
    ? access.find((entry) => String(entry?.MasterRotaId || '') === masterRotaId) || {}
    : {};
}

/** Manage school permission groups and each staff member's single group assignment. */
export default function SysManagerGroups() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [staff, setStaff] = useState([]);
  const [availableTools, setAvailableTools] = useState([]);
  const [bookingGroups, setBookingGroups] = useState([]);
  const [bookingRooms, setBookingRooms] = useState([]);
  const [rotas, setRotas] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(NO_GROUP_ID);
  const [memberSearch, setMemberSearch] = useState('');
  const [showDisabledMembers, setShowDisabledMembers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingCogId, setSavingCogId] = useState('');
  const [groupAction, setGroupAction] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [savingGroup, setSavingGroup] = useState(false);
  const [moveUsersOnDelete, setMoveUsersOnDelete] = useState(false);
  const [replacementGroupId, setReplacementGroupId] = useState('');
  const [savingPermission, setSavingPermission] = useState('');
  const [expandedSections, setExpandedSections] = useState({ permissions: false, users: false });
  const [expandedModules, setExpandedModules] = useState({});

  const canManageGroups = (
    Number(userAppData?.sysManager?.perms ?? 0) === 1
    && userAppData?.sysManager?.staffAdmin === true
  );

  const selectedGroup = groups.find((group) => group.groupId === selectedGroupId) || null;
  const allModulesExpanded = GROUP_PERMISSION_MODULES.every(
    (module) => expandedModules[module.key] === true,
  );
  const groupStaff = useMemo(() => staff.filter((member) => (
    String(member.groupId || '') === selectedGroupId
  )), [selectedGroupId, staff]);
  const displayedStaff = useMemo(() => {
    const searchValue = memberSearch.trim().toLowerCase();
    return groupStaff.filter((member) => {
      if (!showDisabledMembers && member.isDisabled) {
        return false;
      }
      if (!searchValue) {
        return true;
      }
      return [member.displayName, member.email]
        .join(' ')
        .toLowerCase()
        .includes(searchValue);
    });
  }, [groupStaff, memberSearch, showDisabledMembers]);

  /** Select a group and collapse its detail sections until requested. */
  function selectGroup(groupId) {
    setSelectedGroupId(groupId);
    setMemberSearch('');
    setShowDisabledMembers(false);
    setExpandedSections({ permissions: false, users: false });
    setExpandedModules({});
  }

  /** Toggle one independently expandable group-detail section. */
  function toggleSection(sectionName) {
    setExpandedSections((current) => ({
      ...current,
      [sectionName]: !current[sectionName],
    }));
  }

  /** Expand or collapse one module's detailed settings independently. */
  function toggleModule(moduleKey) {
    setExpandedModules((current) => ({
      ...current,
      [moduleKey]: !current[moduleKey],
    }));
  }

  /** Expand every module or collapse them all from the permissions toolbar. */
  function toggleAllModules() {
    const nextExpanded = !allModulesExpanded;
    setExpandedModules(Object.fromEntries(
      GROUP_PERMISSION_MODULES.map((module) => [module.key, nextExpanded]),
    ));
  }

  /** Save one group permission and replace the local group with the API result. */
  async function updateGroupPermission(module, permission, enabled) {
    if (!selectedGroup || savingPermission) {
      return;
    }

    const permissionKey = `${module}.${permission}`;
    setSavingPermission(permissionKey);
    try {
      const response = await updateSysManagerGroups({
        action: 'updatePermission',
        groupId: selectedGroup.groupId,
        module,
        permission,
        enabled,
      });
      setGroups((current) => current.map((group) => (
        group.groupId === response.group.groupId ? response.group : group
      )));
      toast.success('\u2705 Group permission updated.', TOAST_CONFIG);
    } catch (error) {
      console.error('Failed to update group permission', error);
      toast.error(`\u274c ${getGroupsErrorMessage(error, 'Failed to update group permission.')}`, TOAST_CONFIG);
    } finally {
      setSavingPermission('');
    }
  }

  /** Save one catalogue-backed tool assignment for the selected group. */
  async function updateGroupTool(toolGuid, enabled) {
    if (!selectedGroup || savingPermission) {
      return;
    }

    const permissionKey = `toolsData.allowedTools.${toolGuid}`;
    setSavingPermission(permissionKey);
    try {
      const response = await updateSysManagerGroups({
        action: 'updateToolPermission',
        groupId: selectedGroup.groupId,
        toolGuid,
        enabled,
      });
      setGroups((current) => current.map((group) => (
        group.groupId === response.group.groupId ? response.group : group
      )));
      toast.success('\u2705 Group tool access updated.', TOAST_CONFIG);
    } catch (error) {
      console.error('Failed to update group tool access', error);
      toast.error(`\u274c ${getGroupsErrorMessage(error, 'Failed to update group tool access.')}`, TOAST_CONFIG);
    } finally {
      setSavingPermission('');
    }
  }

  /** Save one booking-group access level for the selected permission group. */
  async function updateBookingGroupAccess(bookingGroupId, accessLevel) {
    if (!selectedGroup || savingPermission) {
      return;
    }

    const permissionKey = `bookingsData.groupAccess.${bookingGroupId}`;
    setSavingPermission(permissionKey);
    try {
      const response = await updateSysManagerGroups({
        action: 'updateBookingGroupAccess',
        groupId: selectedGroup.groupId,
        bookingGroupId,
        accessLevel,
      });
      setGroups((current) => current.map((group) => (
        group.groupId === response.group.groupId ? response.group : group
      )));
      toast.success('\u2705 Booking group access updated.', TOAST_CONFIG);
    } catch (error) {
      console.error('Failed to update booking group access', error);
      toast.error(`\u274c ${getGroupsErrorMessage(error, 'Failed to update booking group access.')}`, TOAST_CONFIG);
    } finally {
      setSavingPermission('');
    }
  }

  /** Toggle attendee permissions for one timeslot booking group. */
  async function updateBookingGroupAttendees(bookingGroupId, enabled) {
    if (!selectedGroup || savingPermission) {
      return;
    }
    const permissionKey = `bookingsData.groupAccess.${bookingGroupId}.allowAttendees`;
    setSavingPermission(permissionKey);
    try {
      const response = await updateSysManagerGroups({
        action: 'updateBookingGroupAttendees',
        groupId: selectedGroup.groupId,
        bookingGroupId,
        enabled,
      });
      setGroups((current) => current.map((group) => (
        group.groupId === response.group.groupId ? response.group : group
      )));
      toast.success('\u2705 Booking attendee access updated.', TOAST_CONFIG);
    } catch (error) {
      console.error('Failed to update booking attendee access', error);
      toast.error(`\u274c ${getGroupsErrorMessage(error, 'Failed to update booking attendee access.')}`, TOAST_CONFIG);
    } finally {
      setSavingPermission('');
    }
  }

  /** Save explicit access to one room when whole-group access is not granted. */
  async function updateBookingRoomAccess(roomId, accessLevel) {
    if (!selectedGroup || savingPermission) {
      return;
    }
    const permissionKey = `bookingsData.roomAccess.${roomId}`;
    setSavingPermission(permissionKey);
    try {
      const response = await updateSysManagerGroups({
        action: 'updateBookingRoomAccess',
        groupId: selectedGroup.groupId,
        roomId,
        accessLevel,
      });
      setGroups((current) => current.map((group) => (
        group.groupId === response.group.groupId ? response.group : group
      )));
      toast.success('\u2705 Booking room access updated.', TOAST_CONFIG);
    } catch (error) {
      console.error('Failed to update booking room access', error);
      toast.error(`\u274c ${getGroupsErrorMessage(error, 'Failed to update booking room access.')}`, TOAST_CONFIG);
    } finally {
      setSavingPermission('');
    }
  }

  /** Save the complete inherited settings for one rota family. */
  async function updateRotaAccess(masterRotaId, changes) {
    if (!selectedGroup || savingPermission) return;
    const current = getRotaAccess(selectedGroup.permissions, masterRotaId);
    const payload = {
      accessLevel: String(current.perms || 'none').toLowerCase(),
      allowOverride: current.allowOverride === true,
      availableOnDashboard: current.availableOnDashboard === true,
      ...changes,
    };
    const permissionKey = `rotasData.access.${masterRotaId}`;
    setSavingPermission(permissionKey);
    try {
      const response = await updateSysManagerGroups({
        action: 'updateRotaAccess', groupId: selectedGroup.groupId, masterRotaId, ...payload,
      });
      setGroups((currentGroups) => currentGroups.map((group) => (
        group.groupId === response.group.groupId ? response.group : group
      )));
      toast.success('\u2705 Rota access updated.', TOAST_CONFIG);
    } catch (error) {
      console.error('Failed to update group rota access', error);
      toast.error(`\u274c ${getGroupsErrorMessage(error, 'Failed to update rota access.')}`, TOAST_CONFIG);
    } finally {
      setSavingPermission('');
    }
  }

  async function loadGroups() {
    setLoading(true);
    try {
      const response = await getSysManagerGroups();
      const validGroupIds = new Set(response.groups.map((group) => group.groupId));
      setGroups(response.groups);
      setAvailableTools(response.availableTools);
      setBookingGroups(response.bookingGroups);
      setBookingRooms(response.bookingRooms);
      setRotas(response.rotas);
      // Unknown legacy group references are displayed safely as No Group. This
      // presentation fallback does not alter the stored permissions record.
      setStaff(response.staff.map((member) => ({
        ...member,
        groupId: validGroupIds.has(member.groupId) ? member.groupId : NO_GROUP_ID,
      })));
    } catch (error) {
      console.error('Failed to load permission groups', error);
      toast.error('\u274c Failed to load permission groups.', TOAST_CONFIG);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!canManageGroups) {
      navigate('/app/sysmanager');
      return;
    }
    loadGroups();
  }, [canManageGroups, navigate]);

  async function assignStaff(member, nextGroupId) {
    if (savingCogId || String(member.groupId || '') === nextGroupId) {
      return;
    }
    setSavingCogId(member.cogId);
    try {
      const response = await updateSysManagerGroups({
        action: 'assignMember',
        targetCogId: member.cogId,
        groupId: nextGroupId || null,
      });
      setStaff((current) => current.map((item) => (
        item.cogId === member.cogId ? { ...item, groupId: nextGroupId } : item
      )));
      toast.success(`\u2705 Updated ${member.displayName}'s group.`, TOAST_CONFIG);
    } catch (error) {
      console.error('Failed to update staff group', error);
      toast.error(`\u274c Failed to update ${member.displayName}'s group.`, TOAST_CONFIG);
    } finally {
      setSavingCogId('');
    }
  }

  function openCreateModal() {
    setGroupName('');
    setGroupAction({ type: 'create' });
  }

  function openRenameModal(group) {
    setGroupName(group.name);
    setGroupAction({ type: 'rename', group });
  }

  /** Open the guarded delete flow with reassignment controls reset. */
  function openDeleteModal(group) {
    setMoveUsersOnDelete(false);
    setReplacementGroupId('');
    setGroupAction({ type: 'delete', group });
  }

  async function saveGroup() {
    const cleanName = groupName.trim();
    if (!cleanName || savingGroup) {
      return;
    }
    setSavingGroup(true);
    try {
      const payload = groupAction.type === 'create'
        ? { action: 'createGroup', name: cleanName }
        : { action: 'renameGroup', groupId: groupAction.group.groupId, name: cleanName };
      const response = await updateSysManagerGroups(payload);
      if (groupAction.type === 'create') {
        setGroups((current) => [...current, response.group].sort((a, b) => a.name.localeCompare(b.name)));
        selectGroup(response.group.groupId);
      } else {
        setGroups((current) => current.map((group) => (
          group.groupId === response.group.groupId ? response.group : group
        )).sort((a, b) => a.name.localeCompare(b.name)));
      }
      setGroupAction(null);
      toast.success('\u2705 Permission group saved.', TOAST_CONFIG);
    } catch (error) {
      console.error('Failed to save permission group', error);
      toast.error(`\u274c ${getGroupsErrorMessage(error, 'Failed to save permission group.')}`, TOAST_CONFIG);
    } finally {
      setSavingGroup(false);
    }
  }

  async function deleteGroup(group) {
    if (savingGroup) {
      return;
    }
    setSavingGroup(true);
    try {
      const membersToMove = staff.filter((member) => member.groupId === group.groupId);
      const response = await updateSysManagerGroups({
        action: 'deleteGroup',
        groupId: group.groupId,
        replacementGroupId: membersToMove.length > 0 ? replacementGroupId : null,
      });
      setGroups((current) => current.filter((item) => item.groupId !== group.groupId));
      if (membersToMove.length > 0) {
        setStaff((current) => current.map((member) => (
          member.groupId === group.groupId
            ? { ...member, groupId: replacementGroupId }
            : member
        )));
      }
      setSelectedGroupId(NO_GROUP_ID);
      setGroupAction(null);
      toast.success(
        response.movedUserCount > 0
          ? `\u2705 Moved ${response.movedUserCount} user${response.movedUserCount === 1 ? '' : 's'} and deleted the permission group.`
          : '\u2705 Permission group deleted.',
        TOAST_CONFIG,
      );
    } catch (error) {
      console.error('Failed to delete permission group', error);
      toast.error(`\u274c ${getGroupsErrorMessage(error, 'Failed to delete permission group.')}`, TOAST_CONFIG);
    } finally {
      setSavingGroup(false);
    }
  }

  if (!canManageGroups) {
    return null;
  }

  if (loading) {
    return <LoadingComp />;
  }

  return (
    <div className="main_div">
      <div className="internal_main_div groups-manager-page">
        <Header title="Permission Groups" />
        <button className="groups-manager-back" type="button" onClick={() => navigate('/app/sysmanager')}>
          <FaArrowLeft /> Back to System Manager
        </button>

        <div className="groups-manager-notice">
          Group assignments change only the selected user's group. Existing individual permission data is preserved.
        </div>

        <div className="groups-manager-layout">
          <section className="groups-manager-card groups-manager-sidebar">
            <div className="groups-manager-card-heading">
              <div>
                <h2>Groups</h2>
                <p>Select a group to manage its members.</p>
              </div>
              <button className="groups-manager-primary" type="button" onClick={openCreateModal}>
                <FaPlus /> New group
              </button>
            </div>

            <button
              type="button"
              className={`groups-manager-group ${selectedGroupId === NO_GROUP_ID ? 'is-selected' : ''}`}
              onClick={() => selectGroup(NO_GROUP_ID)}
            >
              <span><strong>No Group</strong><small>Built in - no inherited permissions</small></span>
              <b>{staff.filter((member) => !member.groupId && !member.isDisabled).length}</b>
            </button>

            {groups.map((group) => (
              <button
                type="button"
                className={`groups-manager-group ${selectedGroupId === group.groupId ? 'is-selected' : ''}`}
                key={group.groupId}
                onClick={() => selectGroup(group.groupId)}
              >
                <span><strong>{group.name}</strong><small>{group.description || 'Permission group'}</small></span>
                <b>{staff.filter((member) => (
                  member.groupId === group.groupId && !member.isDisabled
                )).length}</b>
              </button>
            ))}
          </section>

          <section className="groups-manager-card groups-manager-members">
            <div className="groups-manager-card-heading">
              <div>
                <h2>{selectedGroup?.name || 'No Group'}</h2>
                <p>{groupStaff.length} staff member{groupStaff.length === 1 ? '' : 's'}</p>
              </div>
              {selectedGroup && (
                <div className="groups-manager-heading-actions">
                  <button type="button" onClick={() => openRenameModal(selectedGroup)}><FaEdit /> Rename</button>
                  <button className="groups-manager-danger" type="button" onClick={() => openDeleteModal(selectedGroup)}><FaTrash /> Delete</button>
                </div>
              )}
            </div>

            <div className="groups-manager-accordions">
                <section className="groups-manager-accordion">
                  <button
                    type="button"
                    className="groups-manager-accordion-toggle"
                    aria-expanded={expandedSections.permissions}
                    aria-controls="groups-manager-permissions-panel"
                    onClick={() => toggleSection('permissions')}
                  >
                    <span>
                      <strong>Group permissions</strong>
                      <small>Set module access and group capabilities</small>
                    </span>
                    {expandedSections.permissions ? <FaChevronDown aria-hidden="true" /> : <FaChevronRight aria-hidden="true" />}
                  </button>
                  {expandedSections.permissions && (
                    <div className="groups-manager-accordion-content" id="groups-manager-permissions-panel">
                      {!selectedGroup ? (
                        <div className="groups-manager-empty">No Group does not inherit group permissions.</div>
                      ) : (
                        <div className="groups-manager-permissionModules">
                          <div className="groups-manager-permissionModulesToolbar">
                            <button type="button" onClick={toggleAllModules}>
                              <FaChevronDown
                                className={allModulesExpanded ? 'is-expanded' : ''}
                                aria-hidden="true"
                              />
                              {allModulesExpanded ? 'Collapse all' : 'Expand all'}
                            </button>
                          </div>
                          {[...GROUP_PERMISSION_MODULES]
                            .sort((first, second) => first.label.localeCompare(second.label))
                            .map((module) => {
                            const moduleEnabled = getPermissionEnabled(selectedGroup.permissions, module.key, 'perms');
                            const moduleExpanded = expandedModules[module.key] === true;
                            const ModuleIcon = module.icon;
                            return (
                              <section className="groups-manager-permissionModule" key={module.key}>
                                <div className="groups-manager-permissionModuleHeader">
                                  <div className="groups-manager-permissionModuleDetails">
                                    <span className="groups-manager-permissionModuleIcon" aria-hidden="true">
                                      <ModuleIcon />
                                    </span>
                                    <div>
                                      <strong>{module.label}</strong>
                                      <small>{module.description}</small>
                                    </div>
                                  </div>
                                  <div className="groups-manager-permissionModuleActions">
                                    <PolarSwitch
                                      ariaLabel={`${module.label} group access`}
                                      data={moduleEnabled}
                                      loading={savingPermission === `${module.key}.perms`}
                                      disabled={Boolean(savingPermission)}
                                      setData={(enabled) => updateGroupPermission(module.key, 'perms', enabled)}
                                    />
                                    <button
                                      type="button"
                                      className={`groups-manager-permissionModuleToggle ${moduleExpanded ? 'is-expanded' : ''}`}
                                      aria-label={`${moduleExpanded ? 'Hide' : 'Show'} ${module.label} settings`}
                                      aria-expanded={moduleExpanded}
                                      aria-controls={`groups-manager-module-${module.key}`}
                                      onClick={() => toggleModule(module.key)}
                                    >
                                      <FaChevronDown aria-hidden="true" />
                                    </button>
                                  </div>
                                </div>
                                {moduleExpanded && (
                                  <div className="groups-manager-permissionOptions" id={`groups-manager-module-${module.key}`}>
                                    {!moduleEnabled ? (
                                      <div className="groups-manager-moduleDisabledMessage">
                                        Turn on {module.label} access to configure these settings.
                                      </div>
                                    ) : (
                                      <>
                                    {module.options.map(([permission, label, description]) => (
                                      <div className="groups-manager-permissionOption" key={permission}>
                                        <span className="groups-manager-permissionOptionText">
                                          <strong>{label}</strong>
                                          {description && <small>{description}</small>}
                                        </span>
                                        <PolarSwitch
                                          ariaLabel={`${module.label} - ${label}`}
                                          data={getPermissionEnabled(selectedGroup.permissions, module.key, permission)}
                                          loading={savingPermission === `${module.key}.${permission}`}
                                          disabled={Boolean(savingPermission)}
                                          setData={(enabled) => updateGroupPermission(module.key, permission, enabled)}
                                        />
                                      </div>
                                    ))}
                                    {module.key === 'bookingsData' && (
                                      <div className="groups-manager-bookingPermissions">
                                        <div className="groups-manager-bookingPermissionGuide">
                                          <strong>Booking group access</strong>
                                          <p><b>No Access:</b> The booking group is not visible to the user.</p>
                                          <p><b>View:</b> They can see the timetable and bookings but cannot make a booking.</p>
                                          <p><b>Edit:</b> They can book available slots and manage bookings they created.</p>
                                          <p><b>Manage:</b> They can edit any booking or slot and manage rooms and groups data.</p>
                                        </div>
                                        {bookingGroups.length === 0 ? (
                                          <div className="groups-manager-toolEmpty">No booking groups are configured for this school.</div>
                                        ) : bookingGroups.map((bookingGroup) => {
                                          const permissionKey = `bookingsData.groupAccess.${bookingGroup.groupId}`;
                                          const accessLevel = getBookingGroupAccess(
                                            selectedGroup.permissions,
                                            bookingGroup.groupId,
                                          );
                                          const attendeesPermissionKey = `${permissionKey}.allowAttendees`;
                                          return (
                                            <div className="groups-manager-bookingPermission" key={bookingGroup.groupId}>
                                              <span>
                                                <strong>{bookingGroup.name}</strong>
                                                {bookingGroup.type && <small>{bookingGroup.type}</small>}
                                              </span>
                                              <select
                                                value={accessLevel}
                                                disabled={Boolean(savingPermission)}
                                                aria-label={`${bookingGroup.name} booking access`}
                                                onChange={(event) => updateBookingGroupAccess(
                                                  bookingGroup.groupId,
                                                  event.target.value,
                                                )}
                                              >
                                                {BOOKING_ACCESS_OPTIONS.map((option) => (
                                                  <option key={option.value} value={option.value}>{option.label}</option>
                                                ))}
                                              </select>
                                              {savingPermission === permissionKey && <span className="groups-manager-bookingSaving">Saving...</span>}
                                              {String(bookingGroup.type || '').toUpperCase() === 'TIMESLOT' && accessLevel !== 'none' && (
                                                <div className="groups-manager-bookingAttendees">
                                                  <span className="groups-manager-permissionOptionText">
                                                    <strong>Allow attendees</strong>
                                                    <small>Allows users to add and manage attendees when creating or editing bookings in this timeslot group.</small>
                                                  </span>
                                                  <PolarSwitch
                                                    ariaLabel={`Allow attendees for ${bookingGroup.name}`}
                                                    data={getBookingGroupAttendees(
                                                      selectedGroup.permissions,
                                                      bookingGroup.groupId,
                                                    )}
                                                    loading={savingPermission === attendeesPermissionKey}
                                                    disabled={Boolean(savingPermission)}
                                                    setData={(enabled) => updateBookingGroupAttendees(
                                                      bookingGroup.groupId,
                                                      enabled,
                                                    )}
                                                  />
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                        {bookingGroups.length > 0 && (
                                          <div className="groups-manager-roomPermissions">
                                            <div className="groups-manager-bookingPermissionGuide">
                                              <strong>Specific room access</strong>
                                              <p>Use this when the group should access selected rooms rather than the whole booking group.</p>
                                              <p>Room selectors are available only while their parent booking group is set to No Access.</p>
                                            </div>
                                            {bookingGroups.map((bookingGroup) => {
                                              const accessLevel = getBookingGroupAccess(
                                                selectedGroup.permissions,
                                                bookingGroup.groupId,
                                              );
                                              const rooms = bookingRooms.filter((room) => room.groupId === bookingGroup.groupId);
                                              if (rooms.length === 0) {
                                                return null;
                                              }
                                              return (
                                                <section className="groups-manager-roomPermissionGroup" key={bookingGroup.groupId}>
                                                  <div className="groups-manager-roomPermissionHeading">
                                                    <strong>{bookingGroup.name}</strong>
                                                    {accessLevel !== 'none' && <small>Whole-group {accessLevel} access applies.</small>}
                                                  </div>
                                                  {accessLevel === 'none' && rooms.map((room) => {
                                                    const permissionKey = `bookingsData.roomAccess.${room.roomId}`;
                                                    return (
                                                      <div className="groups-manager-roomPermission" key={room.roomId}>
                                                        <span>{room.name}</span>
                                                        <select
                                                          value={getBookingRoomAccess(selectedGroup.permissions, room.roomId)}
                                                          disabled={Boolean(savingPermission)}
                                                          aria-label={`${room.name} room access`}
                                                          onChange={(event) => updateBookingRoomAccess(
                                                            room.roomId,
                                                            event.target.value,
                                                          )}
                                                        >
                                                          {BOOKING_ACCESS_OPTIONS.map((option) => (
                                                            <option key={option.value} value={option.value}>{option.label}</option>
                                                          ))}
                                                        </select>
                                                        {savingPermission === permissionKey && <span className="groups-manager-bookingSaving">Saving...</span>}
                                                      </div>
                                                    );
                                                  })}
                                                </section>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    {module.key === 'toolsData' && (
                                      <div className="groups-manager-toolPermissions">
                                        <div className="groups-manager-toolPermissionsHeading">
                                          <strong>Available tools</strong>
                                          <small>Choose which tools members of this group can use.</small>
                                        </div>
                                        {availableTools.length === 0 ? (
                                          <div className="groups-manager-toolEmpty">No tools are configured for this school.</div>
                                        ) : availableTools.map((tool) => {
                                          const allowedTools = Array.isArray(selectedGroup.permissions?.toolsData?.allowedTools)
                                            ? selectedGroup.permissions.toolsData.allowedTools
                                            : [];
                                          const permissionKey = `toolsData.allowedTools.${tool.guid}`;
                                          const toolEnabled = allowedTools.includes(tool.guid);
                                          return (
                                            <div className="groups-manager-toolPermission" key={tool.guid}>
                                              <div className="groups-manager-permissionOption">
                                                <span>{tool.name}</span>
                                                <PolarSwitch
                                                  ariaLabel={`Allow ${tool.name} for this group`}
                                                  data={toolEnabled}
                                                  loading={savingPermission === permissionKey}
                                                  disabled={Boolean(savingPermission)}
                                                  setData={(enabled) => updateGroupTool(tool.guid, enabled)}
                                                />
                                              </div>
                                              {tool.guid === BULK_EMAIL_TOOL_GUID && toolEnabled && (
                                                <div className="groups-manager-toolPermissionOption">
                                                  <span>Limit email to approved domains</span>
                                                  <PolarSwitch
                                                    ariaLabel="Limit Bulk Email to approved domains"
                                                    data={getPermissionEnabled(
                                                      selectedGroup.permissions,
                                                      'toolsData',
                                                      'bulkEmailApprovedDomainsOnly',
                                                    )}
                                                    loading={savingPermission === 'toolsData.bulkEmailApprovedDomainsOnly'}
                                                    disabled={Boolean(savingPermission)}
                                                    setData={(enabled) => updateGroupPermission(
                                                      'toolsData',
                                                      'bulkEmailApprovedDomainsOnly',
                                                      enabled,
                                                    )}
                                                  />
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                    {module.key === 'rotasData' && (
                                      <div className="groups-manager-bookingPermissions">
                                        <div className="groups-manager-bookingPermissionGuide">
                                          <strong>Rota family access</strong>
                                          <p>View Own shows only the member's assignments. View All shows the complete rota.</p>
                                          <p>Allow overrides lets members submit cover changes. Edit grants rota design access.</p>
                                        </div>
                                        {rotas.length === 0 ? (
                                          <div className="groups-manager-toolEmpty">No active rotas are configured for this school.</div>
                                        ) : rotas.map((rota) => {
                                          const access = getRotaAccess(selectedGroup.permissions, rota.masterRotaId);
                                          const accessLevel = String(access.perms || 'none').toLowerCase();
                                          const permissionKey = `rotasData.access.${rota.masterRotaId}`;
                                          return (
                                            <div className="groups-manager-bookingPermission" key={rota.masterRotaId}>
                                              <span><strong>{rota.name}</strong>{rota.module && <small>{rota.module}</small>}</span>
                                              <select
                                                value={ROTA_ACCESS_OPTIONS.some((option) => option.value === accessLevel) ? accessLevel : 'none'}
                                                disabled={Boolean(savingPermission)}
                                                aria-label={`${rota.name} rota access`}
                                                onChange={(event) => updateRotaAccess(rota.masterRotaId, { accessLevel: event.target.value })}
                                              >
                                                {ROTA_ACCESS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                              </select>
                                              {accessLevel !== 'none' && (
                                                <>
                                                  <div className="groups-manager-permissionOption">
                                                    <span>Allow cover overrides</span>
                                                    <PolarSwitch ariaLabel={`Allow overrides for ${rota.name}`} data={access.allowOverride === true} loading={savingPermission === permissionKey} disabled={Boolean(savingPermission)} setData={(enabled) => updateRotaAccess(rota.masterRotaId, { allowOverride: enabled })} />
                                                  </div>
                                                  <div className="groups-manager-permissionOption">
                                                    <span>Show on dashboard</span>
                                                    <PolarSwitch ariaLabel={`Show ${rota.name} on dashboard`} data={access.availableOnDashboard === true} loading={savingPermission === permissionKey} disabled={Boolean(savingPermission)} setData={(enabled) => updateRotaAccess(rota.masterRotaId, { availableOnDashboard: enabled })} />
                                                  </div>
                                                </>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                      </>
                                    )}
                                  </div>
                                )}
                              </section>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </section>

                <section className="groups-manager-accordion">
                  <button
                    type="button"
                    className="groups-manager-accordion-toggle"
                    aria-expanded={expandedSections.users}
                    aria-controls="groups-manager-users-panel"
                    onClick={() => toggleSection('users')}
                  >
                    <span>
                      <strong>Current users</strong>
                      <small>{displayedStaff.length} staff shown</small>
                    </span>
                    {expandedSections.users ? <FaChevronDown aria-hidden="true" /> : <FaChevronRight aria-hidden="true" />}
                  </button>
                  {expandedSections.users && (
                    <div className="groups-manager-accordion-content" id="groups-manager-users-panel">
                      <div className="groups-manager-memberFilters">
                        <input
                          type="search"
                          value={memberSearch}
                          placeholder="Search group members..."
                          aria-label="Search group members"
                          onChange={(event) => setMemberSearch(event.target.value)}
                        />
                        <label>
                          <input
                            type="checkbox"
                            checked={showDisabledMembers}
                            onChange={(event) => setShowDisabledMembers(event.target.checked)}
                          />
                          <span>Show disabled users</span>
                        </label>
                      </div>
                      {displayedStaff.length === 0 && (
                        <div className="groups-manager-empty">
                          {groupStaff.length === 0
                            ? 'There are no staff members in this group.'
                            : 'No group members match the current filters.'}
                        </div>
                      )}
                      {displayedStaff.map((member) => (
                        <div className="groups-manager-member" key={member.cogId}>
                          <span className="groups-manager-avatar"><FaUsers /></span>
                          <span className="groups-manager-member-details">
                            <strong>{member.displayName}</strong>
                            <small>{member.email}{member.isDisabled ? ' - Deactivated' : ''}</small>
                          </span>
                          <label>
                            <span>Permission group</span>
                            <select
                              value={member.groupId || NO_GROUP_ID}
                              disabled={savingCogId === member.cogId}
                              onChange={(event) => assignStaff(member, event.target.value)}
                            >
                              <option value={NO_GROUP_ID}>No Group</option>
                              {groups.map((group) => <option key={group.groupId} value={group.groupId}>{group.name}</option>)}
                            </select>
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
            </div>
          </section>
        </div>

        {groupAction && (
          <div className="groups-manager-modal-backdrop" role="presentation">
            <div className="groups-manager-modal" role="dialog" aria-modal="true">
              <button className="groups-manager-modal-close" type="button" aria-label="Close" onClick={() => setGroupAction(null)}>x</button>
              {groupAction.type === 'delete' ? (
                <>
                  <h2>Delete {groupAction.group.name}?</h2>
                  {groupStaff.length > 0 ? (
                    <>
                      <p className="groups-manager-delete-warning">
                        This group currently has {groupStaff.length} staff member{groupStaff.length === 1 ? '' : 's'} assigned to it. It cannot be deleted unless they are moved to another group.
                      </p>
                      <label className="groups-manager-delete-move-option">
                        <input
                          type="checkbox"
                          checked={moveUsersOnDelete}
                          onChange={(event) => {
                            setMoveUsersOnDelete(event.target.checked);
                            if (!event.target.checked) setReplacementGroupId('');
                          }}
                        />
                        <span>Move all current users to another group during deletion</span>
                      </label>
                      {moveUsersOnDelete && (
                        <label className="groups-manager-delete-target">
                          New permission group
                          <select value={replacementGroupId} onChange={(event) => setReplacementGroupId(event.target.value)}>
                            <option value="">Select a group...</option>
                            {groups
                              .filter((group) => group.groupId !== groupAction.group.groupId)
                              .map((group) => <option key={group.groupId} value={group.groupId}>{group.name}</option>)}
                          </select>
                        </label>
                      )}
                    </>
                  ) : (
                    <p>No staff are assigned to this group. It can be deleted safely.</p>
                  )}
                  <div className="groups-manager-modal-actions">
                    <button type="button" onClick={() => setGroupAction(null)}>Cancel</button>
                    <button
                      className="groups-manager-danger"
                      type="button"
                      disabled={savingGroup || (groupStaff.length > 0 && (!moveUsersOnDelete || !replacementGroupId))}
                      onClick={() => deleteGroup(groupAction.group)}
                    >
                      {groupStaff.length > 0 ? 'Move Users then Delete' : 'Delete group'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h2>{groupAction.type === 'create' ? 'Create permission group' : 'Rename permission group'}</h2>
                  <label>
                    Group name
                    <input maxLength={80} value={groupName} autoFocus onChange={(event) => setGroupName(event.target.value)} />
                  </label>
                  <div className="groups-manager-modal-actions">
                    <button type="button" onClick={() => setGroupAction(null)}>Cancel</button>
                    <button className="groups-manager-primary" type="button" disabled={!groupName.trim() || savingGroup} onClick={saveGroup}>Save group</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
